import { LiteralArray } from '@angular/compiler';
import { Component, Input, forwardRef, input } from '@angular/core';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

/**
 * Text Input Component
 *
 * Reusable text input with Angular Material form field.
 * Supports ControlValueAccessor for reactive forms integration.
 */
@Component({
  selector: 'app-text-input',
  standalone: true,
  imports: [FormsModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true,
    },
  ],
  template: `
    <mat-form-field class="text-input-field" appearance="outline">
      @if (label()) {
        <mat-label>{{ label() }}</mat-label>
      }
      <input
        matInput
        [formControl]="control()"
        [type]="type()"
        [placeholder]="placeholder()"
        [value]="value"
        (input)="onInput($event)"
        (blur)="onTouched()"
        [attr.autocomplete]="autocomplete()"
      />
      @if (control().invalid && (control().touched || control().dirty)) {
        <mat-error>
          @if (control().hasError('required')) {
            {{ label() || 'This field' }} is required.
          }
          @if (control().hasError('email')) {
            Please enter a valid email address.
          }
        </mat-error>
      }
    </mat-form-field>
  `,
  styles: [`
    .text-input-field {
      width: 100%;
      font-family: var(--aida-ref-font-family-text);
    }

    .text-input-field ::ng-deep .mat-mdc-form-field-error {
      font-size: var(--aida-ref-font-size-300);
      color: var(--aida-colour-on-surface-error);
      margin-top: var(--aida-ref-spacing-50);
    }

    .text-input-field ::ng-deep .mat-mdc-text-field-wrapper {
      background-color: var(--aida-colour-surface-main);
    }

    .text-input-field ::ng-deep .mat-mdc-form-field-focus-overlay {
      background-color: transparent;
    }

    /* Ensure mat-error is visible */
    ::ng-deep mat-error {
      display: block;
      font-size: var(--aida-ref-font-size-300);
    }
  `],
})
export class TextInputComponent implements ControlValueAccessor {
  control = input.required<FormControl<any>>();

  label = input<string>('');
  placeholder = input<string>('')
  type = input<'text'|'email'|'password'>('text');
  errorMessage = input<string | null>();
  autocomplete = input<string>('off');

  value = '';
  disabled = false;

  // ControlValueAccessor implementation
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChange(this.value);
  }
}
